/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getHealth,
  getMe,
  getMonitoringHistory,
  getMonitoringOverview,
  logout,
  refreshMonitoring,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

import { clearPersistedQueryCache } from "./persist";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
      // …and the persisted copy with it. Emptying the in-memory cache would
      // overwrite storage on the persister's next (throttled) write anyway;
      // this removes it now, so the session's data is gone the moment the
      // session is.
      clearPersistedQueryCache();
    },
  });
}

// ─── Monitoring ──────────────────────────────────────────────────────────

/** Key for the estate overview — the mutation below writes straight into it. */
const MONITORING_OVERVIEW_KEY = ["monitoring", "overview"] as const;

export function useMonitoringOverview() {
  return useQuery({
    queryKey: MONITORING_OVERVIEW_KEY,
    queryFn: async () => {
      const { data } = await getMonitoringOverview();
      logger.debug("[monitoring] overview received", data?.summary);
      return data;
    },
    // The backend re-probes on its own interval; poll often enough that the
    // page never shows a stale cycle for long.
    refetchInterval: 30_000,
  });
}

export function useRefreshMonitoring() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await refreshMonitoring();
      return data;
    },
    onSuccess: (data) => {
      // The response *is* the fresh snapshot — seed the cache instead of
      // invalidating and paying for a second round trip.
      queryClient.setQueryData(MONITORING_OVERVIEW_KEY, data);
    },
  });
}

export function useMonitoringHistory(days = 90) {
  return useQuery({
    queryKey: ["monitoring", "history", days],
    queryFn: async () => {
      const { data } = await getMonitoringHistory({ query: { days } });
      return data;
    },
    // Daily rollups only shift as cycles land — no need to poll this hard.
    refetchInterval: 5 * 60_000,
  });
}
